import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink, ImagePlus, Loader2, RotateCcw, Save, Trash2 } from 'lucide-react';
import { GlassCard } from '@/components/design/GlassCard';
import { GlassTabs } from '@/components/design/GlassTabs';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { AdminAPI, API_URL } from '@/services/api';
import {
  ABOUT_PAGE,
  DEFAULTS,
  allFields,
  imageFieldKeys,
  textFieldKeys,
  type FieldDef,
} from './pageSchema';

type Lang = 'az' | 'en';
type FieldMap = Record<string, string>;
type Values = Record<Lang, FieldMap>;

/** Yüklənmiş fayllar API ilə eyni ünvandan verilir, `/api/v1` sonluğu olmadan. */
const FILES_ORIGIN = API_URL.replace(/\/api\/v1\/?$/, '');

/**
 * Saytın ünvanı — «Səhifəyə bax» keçidi üçün.
 * Canlıda panel `admin.politicalmaze.com`-dadır, sayt isə həmin ünvanın
 * `admin.` hissəsi olmayan variantıdır. Lokalda 5011 portudur.
 */
const SITE_URL: string =
  import.meta.env.VITE_SITE_URL ??
  (window.location.hostname.startsWith('admin.')
    ? `${window.location.protocol}//${window.location.hostname.replace(/^admin\./, '')}`
    : 'http://localhost:5011');

const looksLikeHtml = (value: string) => /<(p|div|h[1-6]|ul|ol|li|blockquote|img|br|strong|b|em|i|a)\b/i.test(value);

/**
 * Düz mətni redaktorun anladığı HTML-ə çevirir.
 *
 * İlkin bioqrafiya mətni `\n\n` ilə ayrılmış abzaslardır (saytın i18n faylından
 * gəlir). Redaktor `innerHTML` ilə işlədiyi üçün belə mətn tək blok kimi
 * yapışardı. Çevirmə SABİTDİR — eyni giriş həmişə eyni nəticə verir, yoxsa
 * toxunulmamış sahə «dəyişdirilib» kimi görünərdi.
 */
function htmlFromPlainText(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escape(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** Sxemdəki bütün mətn sahələrini ilkin mətnlərlə doldurur. */
function buildDefaults(): Values {
  const htmlKeys = new Set(
    allFields(ABOUT_PAGE).filter((f) => f.type === 'html').map((f) => f.key),
  );
  const pick = (lang: Lang): FieldMap =>
    Object.fromEntries(
      textFieldKeys(ABOUT_PAGE).map((key) => {
        const raw = DEFAULTS[lang][key] ?? '';
        return [key, htmlKeys.has(key) && !looksLikeHtml(raw) ? htmlFromPlainText(raw) : raw];
      }),
    );
  return { az: pick('az'), en: pick('en') };
}

/**
 * Admin panelindən redaktə olunan səhifələr (hazırda: Haqqımızda).
 *
 * ⚠️ BAZAYA YALNIZ DƏYİŞDİRİLMİŞ SAHƏLƏR YAZILIR. Sahə ilkin mətnlə eynidirsə
 * göndərilmir, yəni sayt onu öz i18n faylından oxuyur. Bunun iki nəticəsi var:
 *   • «İlkin mətnə qayıt» üçün ayrıca endpoint lazım deyil — mətni geri yazmaq bəsdir;
 *   • tərcümələr kodda yenilənəndə toxunulmamış sahələr avtomatik yenilənir.
 *
 * Şəkillər ayrıca `assets` xəritəsindədir, çünki foto hər iki dildə eynidir.
 */
export const PagesEditorPage = () => {
  const page = ABOUT_PAGE;
  const defaults = useMemo(buildDefaults, []);

  const [lang, setLang] = useState<Lang>('az');
  const [values, setValues] = useState<Values>(defaults);
  const [assets, setAssets] = useState<FieldMap>({});
  /** Serverdə olan son vəziyyət — «dəyişiklik varmı?» sualının cavabı üçün */
  const [saved, setSaved] = useState<{ values: Values; assets: FieldMap }>({
    values: defaults,
    assets: {},
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    AdminAPI.pages
      .get(page.key)
      .then((res) => {
        if (cancelled) return;
        const merged: Values = {
          az: { ...defaults.az, ...(res.data?.fieldsAz ?? {}) },
          en: { ...defaults.en, ...(res.data?.fieldsEn ?? {}) },
        };
        const loadedAssets: FieldMap = res.data?.assets ?? {};
        setValues(merged);
        setAssets(loadedAssets);
        setSaved({ values: merged, assets: loadedAssets });
      })
      .catch(() => {
        if (!cancelled) toast.error('Səhifə mətnləri yüklənmədi');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page.key, defaults]);

  const dirty = useMemo(
    () => JSON.stringify({ values, assets }) !== JSON.stringify(saved),
    [values, assets, saved],
  );

  const setField = (key: string, value: string) =>
    setValues((prev) => ({ ...prev, [lang]: { ...prev[lang], [key]: value } }));

  const resetField = (key: string) => setField(key, defaults[lang][key] ?? '');

  const isChanged = (field: FieldDef) =>
    field.type === 'image'
      ? Boolean(assets[field.key])
      : (values[lang][field.key] ?? '').trim() !== (defaults[lang][field.key] ?? '').trim();

  /** Neçə sahə ilkin vəziyyətdən fərqlənir — başlıqdakı sayğac üçün */
  const changedCount = useMemo(
    () => allFields(page).filter(isChanged).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, assets, defaults, lang, page],
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      /* Yalnız ilkin mətndən fərqlənənlər gedir; qalanları serverdə silinir. */
      const diff = (l: Lang): FieldMap => {
        const out: FieldMap = {};
        for (const key of textFieldKeys(page)) {
          const value = (values[l][key] ?? '').trim();
          if (value.length > 0 && value !== (defaults[l][key] ?? '').trim()) out[key] = value;
        }
        return out;
      };

      const usedAssets: FieldMap = {};
      for (const key of imageFieldKeys(page)) {
        if (assets[key]) usedAssets[key] = assets[key];
      }

      await AdminAPI.pages.save(page.key, {
        fieldsAz: diff('az'),
        fieldsEn: diff('en'),
        assets: usedAssets,
      });
      setSaved({ values, assets });
      toast.success('Səhifə yadda saxlanıldı');
    } catch {
      toast.error('Yadda saxlanmadı — yenidən cəhd edin');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-3" />
        Yüklənir...
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-28">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Səhifələr</h1>
          <p className="text-muted-foreground mt-1">
            «{page.title}» səhifəsinin mətnləri və şəkilləri. Dizayn dəyişmir.
          </p>
        </div>
        <a
          href={`${SITE_URL}${page.path}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          Səhifəyə bax
        </a>
      </div>

      <GlassCard className="p-4">
        <div className="flex flex-wrap items-center gap-4">
          <GlassTabs
            className="w-64"
            activeTab={lang}
            onChange={(id) => setLang(id as Lang)}
            tabs={[
              { id: 'az', label: 'Azərbaycanca' },
              { id: 'en', label: 'English' },
            ]}
          />
          <p className="text-sm text-muted-foreground">
            {changedCount > 0
              ? `${changedCount} sahə dəyişdirilib`
              : 'Bütün sahələr ilkin vəziyyətdədir'}
          </p>
          <p className="text-xs text-muted-foreground ml-auto">
            Şəkillər hər iki dildə eynidir — dil dəyişəndə yenidən yükləmək lazım deyil.
          </p>
        </div>
      </GlassCard>

      {page.sections.map((section) => (
        <GlassCard key={section.title}>
          <h2 className="text-xl font-semibold mb-1">{section.title}</h2>
          {section.note && <p className="text-sm text-amber-400/90 mb-5">{section.note}</p>}
          <div className="space-y-6 mt-5">
            {section.fields.map((field) => (
              <FieldRow
                key={field.key}
                field={field}
                value={field.type === 'image' ? (assets[field.key] ?? '') : (values[lang][field.key] ?? '')}
                changed={isChanged(field)}
                onChange={(value) =>
                  field.type === 'image'
                    ? setAssets((prev) => ({ ...prev, [field.key]: value }))
                    : setField(field.key, value)
                }
                onReset={() =>
                  field.type === 'image'
                    ? setAssets((prev) => ({ ...prev, [field.key]: '' }))
                    : resetField(field.key)
                }
              />
            ))}
          </div>
        </GlassCard>
      ))}

      {/* Yadda saxlama zolağı — uzun səhifədə yuxarı qayıtmamaq üçün aşağıda sabitdir */}
      <div className="fixed bottom-0 left-64 right-0 z-40 border-t border-white/10 bg-background/80 backdrop-blur px-8 py-4 flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {dirty ? 'Yadda saxlanmamış dəyişiklik var' : 'Hər şey yadda saxlanılıb'}
        </p>
        <Button onClick={handleSave} disabled={!dirty || saving} className="min-w-40">
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Yadda saxla
        </Button>
      </div>
    </div>
  );
};

interface FieldRowProps {
  field: FieldDef;
  value: string;
  changed: boolean;
  onChange: (value: string) => void;
  onReset: () => void;
}

const FieldRow = ({ field, value, changed, onChange, onReset }: FieldRowProps) => (
  <div>
    <div className="flex items-center justify-between gap-3 mb-2">
      <label className="text-sm font-medium flex items-center gap-2">
        {field.label}
        {changed && (
          <span className="text-[11px] font-normal px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/30">
            dəyişdirilib
          </span>
        )}
      </label>
      {changed && field.type !== 'image' && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          İlkin mətnə qayıt
        </button>
      )}
    </div>

    {/* `data-field` avtomatik sınaqların sahəni tapması üçündür */}
    {field.type === 'image' ? (
      <ImageField fieldKey={field.key} value={value} onChange={onChange} onClear={onReset} />
    ) : field.type === 'html' ? (
      <div
        data-field={field.key}
        className="rounded-md border border-input bg-background overflow-hidden"
      >
        <RichTextEditor value={value} onChange={onChange} />
      </div>
    ) : field.type === 'text' ? (
      <Input data-field={field.key} value={value} onChange={(e) => onChange(e.target.value)} />
    ) : (
      <Textarea
        data-field={field.key}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={field.type === 'paragraphs' ? 18 : 3}
      />
    )}

    {field.hint && <p className="text-xs text-muted-foreground mt-1.5">{field.hint}</p>}
  </div>
);

interface ImageFieldProps {
  fieldKey: string;
  /** `/uploads/...` yolu, boşdursa şəkil seçilməyib */
  value: string;
  onChange: (path: string) => void;
  onClear: () => void;
}

/**
 * Şəkil yükləmə sahəsi.
 *
 * ⚠️ Bazaya TAM ÜNVAN yox, `/uploads/...` yolu yazılır — domen dəyişsə
 * şəkillər sınmasın. Ekranda göstərmək üçün ünvan burada yığılır.
 */
const ImageField = ({ fieldKey, value, onChange, onClear }: ImageFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handlePick = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // eyni faylı təkrar seçmək mümkün olsun
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Şəkil 5 MB-dan böyük olmamalıdır');
      return;
    }

    try {
      setUploading(true);
      const res = await AdminAPI.uploads.uploadImage(file);
      onChange(res.data.url); // `/uploads/xxx.jpg`
      toast.success('Şəkil yükləndi — «Yadda saxla» düyməsini basmağı unutmayın');
    } catch {
      toast.error('Şəkli yükləmək alınmadı');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      {value ? (
        <img
          src={`${FILES_ORIGIN}${value}`}
          alt=""
          className="w-20 h-20 rounded-full object-cover border-4 border-primary/20 shrink-0"
        />
      ) : (
        <div className="w-20 h-20 rounded-full border-4 border-dashed border-white/15 flex items-center justify-center text-muted-foreground shrink-0">
          <ImagePlus className="w-6 h-6" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <ImagePlus className="w-4 h-4 mr-2" />
          )}
          {value ? 'Şəkli dəyiş' : 'Şəkil seç'}
        </Button>

        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={onClear}>
            <Trash2 className="w-4 h-4 mr-2" />
            Şəkli sil
          </Button>
        )}

        {!value && (
          <span className="text-xs text-muted-foreground">
            Boş qalsa kartda baş hərflər görünəcək
          </span>
        )}
      </div>

      <input
        ref={inputRef}
        data-field={fieldKey}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handlePick}
      />
    </div>
  );
};
